'use client'

import { Bump24, Chart24, PriceTag24, TargetArrow24, X24 } from '@vinted/monochrome-icons'
import {
  Button,
  Cell,
  Dialog,
  Divider,
  Icon,
  Image,
  Navigation,
  Spacer,
  Text,
} from '@vinted/web-ui'
import { IconType } from '@vinted/web-ui/dist/types/lib/Icon/Icon'
import { ReactNode } from 'react'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { useAsset } from '@marketplace-web/shared/assets'
import { HelpCenterFaqEntryUrl } from '@marketplace-web/help-center/help-center-faq-entry-url-feature'
import {
  AccessChannel,
  FaqEntryType,
} from '@marketplace-web/help-center/help-center-faq-entry-url-data'
import { useAbTest, useTrackAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

type Props = {
  show: boolean
  hasLocalOptions?: boolean
  onCloseOrConfirm: () => void
}

type ContentRow = {
  icon: IconType
  text: string
}

const BumpValuePropositionModal = ({ show, hasLocalOptions, onCloseOrConfirm }: Props) => {
  const translate = useTranslate('bump_items.value_proposition_modal_v2')
  const a11yTranslate = useTranslate('bump_items.a11y')
  const asset = useAsset('/assets/bumps')

  const bumpValuePropositionUpdateAbTest = useAbTest('bump_value_proposition_update')

  useTrackAbTest(bumpValuePropositionUpdateAbTest, show)

  const learnMoreTranslationValues = {
    'help-center': (chunks: Array<ReactNode>) => (
      <HelpCenterFaqEntryUrl
        key="help-center"
        type={FaqEntryType.Bump}
        accessChannel={AccessChannel.ProductLink}
        render={url => (
          <a href={url} target="_blank" rel="noreferrer">
            {chunks}
          </a>
        )}
      />
    ),
  }

  const contentRows: Array<ContentRow> = [
    { icon: Bump24, text: translate('body.one') },
    { icon: TargetArrow24, text: translate('body.two') },
    { icon: Chart24, text: translate('body.three') },
    { icon: PriceTag24, text: translate(`body.four${hasLocalOptions ? '_international' : ''}`) },
  ]

  const handleOnClose = () => {
    onCloseOrConfirm()
  }

  const renderModalBody = () => {
    const imageSrc = asset('new-value-proposition.png', {
      theme: { dark: 'new-value-proposition-dark.png' },
    })
    const imageSrc2x = asset('new-value-proposition@2x.png', {
      theme: { dark: 'new-value-proposition-dark@2x.png' },
    })
    const imageSrcSet = `${imageSrc} 1x, ${imageSrc2x} 2x`

    return (
      <div className="u-fill-width">
        <Navigation
          body={translate('title')}
          right={
            <Button
              styling="flat"
              inline
              aria={{ 'aria-label': a11yTranslate('actions.close') }}
              iconName={X24}
              onClick={handleOnClose}
            />
          }
        />
        <Divider />
        <Image src={imageSrc} srcset={imageSrcSet} scaling="cover" ratio="landscape" />
        <Cell styling="wide">
          <SeparatedList separator={<Spacer size="large" />}>
            {contentRows.map(({ icon, text }) => (
              <div key={`body-bullet-${icon.Title}`} className="u-flexbox u-gap-medium">
                <Icon color="greyscale-level-2" name={icon} title={icon.Title} />
                <Text as="span" html text={text} type="body" />
              </div>
            ))}
          </SeparatedList>
          <Spacer size="x2-large" />
          <Text as="span" type="body">
            {translate('actions.learn_more', learnMoreTranslationValues)}
          </Text>
          <Spacer size="x2-large" />
          <Button
            text={translate('actions.confirm')}
            styling="filled"
            size="medium"
            onClick={onCloseOrConfirm}
            testId="bump-confirm-button"
          />
        </Cell>
      </div>
    )
  }

  return (
    <Dialog hasScrollableContent defaultCallback={handleOnClose} show={show}>
      {renderModalBody()}
    </Dialog>
  )
}

export default BumpValuePropositionModal
